;(() => {
  const componentName = 'cg-starter-pack'

  if (customElements.get(componentName)) return

  class CgStarterPack extends HTMLElement {
    constructor() {
      super()

      this.carousel = this.querySelector('[data-cg-starter-pack-carousel]')
      this.scrollbar = this.querySelector('[data-cg-starter-pack-scrollbar]')
      this.thumb = this.querySelector('[data-cg-starter-pack-scrollbar-thumb]')
      this.frame = null
      this.updateScrollbar = this.updateScrollbar.bind(this)
    }

    connectedCallback() {
      if (!this.carousel || !this.scrollbar || !this.thumb) return

      this.controller = new AbortController()
      const { signal } = this.controller

      this.carousel.addEventListener('scroll', this.updateScrollbar, {
        passive: true,
        signal
      })
      window.addEventListener('resize', this.updateScrollbar, { signal })

      this.resizeObserver = new ResizeObserver(this.updateScrollbar)
      this.resizeObserver.observe(this.carousel)
      this.resizeObserver.observe(this.scrollbar)

      this.updateScrollbar()
    }

    disconnectedCallback() {
      this.controller?.abort()
      this.resizeObserver?.disconnect()

      if (this.frame) cancelAnimationFrame(this.frame)
    }

    updateScrollbar() {
      if (this.frame) cancelAnimationFrame(this.frame)

      this.frame = requestAnimationFrame(() => {
        const { clientWidth, scrollLeft, scrollWidth } = this.carousel
        const trackWidth = this.scrollbar.clientWidth
        const maxScroll = scrollWidth - clientWidth

        if (!trackWidth) return

        const thumbWidth =
          maxScroll > 0
            ? Math.max(48, (clientWidth / scrollWidth) * trackWidth)
            : trackWidth
        const maxOffset = trackWidth - thumbWidth
        const offset = maxScroll > 0 ? (scrollLeft / maxScroll) * maxOffset : 0

        this.thumb.style.width = `${thumbWidth}px`
        this.thumb.style.transform = `translateX(${offset}px)`
      })
    }
  }

  customElements.define(componentName, CgStarterPack)
})()
;(() => {
  const componentName = 'try-inspiration-carousel'
  const openVideoDialogs = new Set()

  if (customElements.get(componentName)) return

  class TryInspirationCarousel extends HTMLElement {
    constructor() {
      super()

      this.track = this.querySelector('[data-inspiration-track]')
      this.cards = Array.from(this.querySelectorAll('[data-inspiration-card]'))
      this.previousButton = this.querySelector('[data-inspiration-previous]')
      this.nextButton = this.querySelector('[data-inspiration-next]')
      this.videoTriggers = Array.from(
        this.querySelectorAll('[data-inspiration-video-trigger]')
      )
      this.videoDialog = this.querySelector('[data-inspiration-video-dialog]')
      this.video = this.querySelector('[data-inspiration-video]')
      this.videoCloseButton = this.querySelector(
        '[data-inspiration-video-close]'
      )
      this.frame = null
      this.updateControls = this.updateControls.bind(this)
      this.closeVideo = this.closeVideo.bind(this)
      this.resetVideo = this.resetVideo.bind(this)
    }

    connectedCallback() {
      if (
        !this.track ||
        !this.cards.length ||
        !this.previousButton ||
        !this.nextButton
      )
        return

      this.controller = new AbortController()
      const { signal } = this.controller

      this.previousButton.addEventListener('click', () => this.scroll(-1), {
        signal
      })
      this.nextButton.addEventListener('click', () => this.scroll(1), {
        signal
      })
      this.videoTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.openVideo(trigger), {
          signal
        })
      })
      this.videoCloseButton?.addEventListener('click', this.closeVideo, {
        signal
      })
      this.videoDialog?.addEventListener(
        'click',
        (event) => {
          if (event.target === this.videoDialog) this.closeVideo()
        },
        { signal }
      )
      this.videoDialog?.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault()
          this.closeVideo()
        },
        { signal }
      )
      this.track.addEventListener('scroll', this.updateControls, {
        passive: true,
        signal
      })
      window.addEventListener('resize', this.updateControls, { signal })

      this.resizeObserver = new ResizeObserver(this.updateControls)
      this.resizeObserver.observe(this.track)
      this.updateControls()
    }

    disconnectedCallback() {
      this.closeVideo(false)

      this.controller?.abort()
      this.resizeObserver?.disconnect()

      if (this.frame) cancelAnimationFrame(this.frame)
    }

    scroll(direction) {
      const cardWidth = this.cards[0].getBoundingClientRect().width
      const styles = getComputedStyle(this.track)
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0

      this.track.scrollBy({
        behavior: 'smooth',
        left: direction * (cardWidth + gap)
      })
    }

    openVideo(trigger) {
      const source = trigger.dataset.videoSrc

      if (
        !source ||
        !this.videoDialog ||
        !this.video ||
        this.videoDialog.open
      )
        return

      this.previouslyFocused = document.activeElement
      this.video.src = source
      this.video.load()
      this.videoDialog.showModal()
      openVideoDialogs.add(this.videoDialog)
      document.documentElement.classList.add('overflow-hidden')
      document.body.classList.add('overflow-hidden')
      this.videoCloseButton?.focus({ preventScroll: true })
      this.video.play().catch(() => {})
    }

    closeVideo(restoreFocus = true) {
      if (!this.videoDialog) return

      if (this.videoDialog.open) this.videoDialog.close()

      this.resetVideo()
      openVideoDialogs.delete(this.videoDialog)

      if (openVideoDialogs.size === 0) {
        document.documentElement.classList.remove('overflow-hidden')
        document.body.classList.remove('overflow-hidden')
      }

      if (restoreFocus && this.previouslyFocused instanceof HTMLElement) {
        this.previouslyFocused.focus({ preventScroll: true })
      }

      this.previouslyFocused = null
    }

    resetVideo() {
      if (!this.video) return

      this.video.pause()
      this.video.removeAttribute('src')
      this.video.load()
    }

    updateControls() {
      if (this.frame) cancelAnimationFrame(this.frame)

      this.frame = requestAnimationFrame(() => {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth
        const atStart = this.track.scrollLeft <= 1
        const atEnd = this.track.scrollLeft >= maxScroll - 1

        this.syncButton(this.previousButton, atStart)
        this.syncButton(this.nextButton, atEnd)
      })
    }

    syncButton(button, disabled) {
      button.disabled = disabled
      const disabledArrow = button.querySelector('[data-arrow-disabled]')
      const enabledArrow = button.querySelector('[data-arrow-enabled]')

      if (disabledArrow) disabledArrow.hidden = !disabled
      if (enabledArrow) enabledArrow.hidden = disabled
    }
  }

  customElements.define(componentName, TryInspirationCarousel)
})()

;(() => {
  const parseTarget = (value) => {
    const match = String(value || '')
      .trim()
      .match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})$/)

    return match
      ? { year: +match[1], month: +match[2], day: +match[3], hour: +match[4], minute: +match[5] }
      : null
  }

  const timezoneOffset = (timezone, date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(date)
      .reduce((result, part) => {
        result[part.type] = part.value
        return result
      }, {})
    const utcTime = Date.UTC(
      +parts.year,
      +parts.month - 1,
      +parts.day,
      +parts.hour % 24,
      +parts.minute,
      +parts.second,
    )

    return utcTime - date.getTime()
  }

  const zonedToUtc = (target, timezone) => {
    const guess = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, 0)
    let timestamp = guess - timezoneOffset(timezone, new Date(guess))
    timestamp = guess - timezoneOffset(timezone, new Date(timestamp))

    return timestamp
  }

  const initTimer = (root) => {
    const hours = root.querySelector('[data-try-limited-time-offer-hours]')
    const minutes = root.querySelector('[data-try-limited-time-offer-minutes]')
    const seconds = root.querySelector('[data-try-limited-time-offer-seconds]')
    const text = root.querySelector('[data-try-limited-time-offer-text]')
    const hideWhenExpired = root.dataset.hideWhenExpired === 'true'
    const expiredMessage = root.dataset.expiredMessage

    if (!hours || !minutes || !seconds) return

    // Evergreen countdown: each visitor gets a rolling 24-hour timer stored in
    // sessionStorage so it persists across page refreshes but resets each session.
    const STORAGE_KEY = 'cg_try_timer_end'
    const DURATION_MS = 24 * 60 * 60 * 1000
    let target
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored && Number(stored) > Date.now()) {
      target = Number(stored)
    } else {
      target = Date.now() + DURATION_MS
      sessionStorage.setItem(STORAGE_KEY, String(target))
    }

    const render = (hoursValue, minutesValue, secondsValue) => {
      hours.textContent = String(hoursValue).padStart(2, '0')
      minutes.textContent = String(minutesValue).padStart(2, '0')
      seconds.textContent = String(secondsValue).padStart(2, '0')
    }

    const updateTimer = () => {
      const difference = target - Date.now()

      if (difference <= 0) {
        if (hideWhenExpired) {
          root.hidden = true
        } else {
          render(0, 0, 0)
          if (expiredMessage && text) text.textContent = expiredMessage
        }
        return false
      }

      const totalSeconds = Math.floor(difference / 1000)
      render(
        Math.floor(totalSeconds / 3600),
        Math.floor((totalSeconds % 3600) / 60),
        totalSeconds % 60,
      )
      return true
    }

    if (!updateTimer()) return

    const interval = window.setInterval(() => {
      if (!updateTimer()) window.clearInterval(interval)
    }, 1000)

    window.addEventListener(
      'pagehide',
      () => window.clearInterval(interval),
      { once: true },
    )
  }

  const initTimers = () => {
    document.querySelectorAll('[data-try-limited-time-offer]').forEach(initTimer)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTimers)
  } else {
    initTimers()
  }
})()

// cg-video-intro is defined via {% javascript %} in its section file, which
// Shopify only bundles for templates that render the section. Landing pages
// that A/B-inject it need the definition available here so connectedCallback
// fires on the injected element.
;(() => {
  if (customElements.get('cg-video-intro')) return

  const openVideoDialogs = new Set()

  customElements.define(
    'cg-video-intro',
    class CgVideoIntro extends HTMLElement {
      connectedCallback() {
        this.controller?.abort()
        this.controller = new AbortController()

        this.playButton = this.querySelector('[data-video-play]')
        this.dialog = this.querySelector('[data-video-dialog]')
        this.closeButton = this.querySelector('[data-video-close]')
        this.frame = this.querySelector('[data-video-frame]')

        if (!this.playButton || !this.dialog || !this.closeButton || !this.frame) return

        const { signal } = this.controller

        this.playButton.addEventListener('click', () => this.openDialog(), { signal })
        this.closeButton.addEventListener('click', () => this.closeDialog(), { signal })
        this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.closeDialog() }, { signal })
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeDialog() }, { signal })
      }

      disconnectedCallback() {
        this.closeDialog(false)
        this.controller?.abort()
      }

      openDialog() {
        const videoUrl = this.dataset.videoUrl
        if (!videoUrl || this.dialog.open) return
        this.previouslyFocused = document.activeElement
        this.frame.src = videoUrl
        this.dialog.showModal()
        openVideoDialogs.add(this.dialog)
        document.documentElement.classList.add('overflow-hidden')
        document.body.classList.add('overflow-hidden')
        this.closeButton.focus({ preventScroll: true })
      }

      closeDialog(restoreFocus = true) {
        if (!this.dialog) return
        if (this.dialog.open) this.dialog.close()
        if (this.frame) this.frame.src = 'about:blank'
        openVideoDialogs.delete(this.dialog)
        if (openVideoDialogs.size === 0) {
          document.documentElement.classList.remove('overflow-hidden')
          document.body.classList.remove('overflow-hidden')
        }
        if (restoreFocus && this.previouslyFocused instanceof HTMLElement) {
          this.previouslyFocused.focus({ preventScroll: true })
        }
      }
    }
  )
})()
